import { clearObject } from "@/modules/utils";
import { useApiWithAuth } from "@/modules/api";
import { useCache } from "@/modules/cache";

interface LicencaFaturamentoFilterPayload {
  idCliente?: number;
  numeroPedidoOrgao?: string;
  idTransportador?: number;
  periodo?: Date;
  statusFatura?: string;
}

interface TipoLicencaFaturamentoPayload {
  flag?: string;
  mensagem?: string;
}

interface LicencaFaturamentoPayload {
  idFaturamentoItem?: number | null;
  idLicencaOrgao?: number | null;
  idLicenca?: number | null;
  idFaturamento?: number | null;
  flTipoItemStr?: string | null;
  idOrgao?: number | null;
  statusFaturaStr?: string | null;
  idTransportador?: number | null;
  nome?: string | null;
  cpfCnpj?: number | null;
  numeroPedidoOrgao?: string | null;
  faturamentoItem?: LicencaFaturamentoFilterReturnPayload | null;
  groupIndex?: number | null;
}

interface LicencaFaturamentoFilterReturnPayload {
  idFaturamentoItem: number | null;
  idLicencaOrgao: number | null;
  idLicenca: number | null;
  idFaturamento: number | null;
  flTipoItem: string | null;
  idOrgao: number | null;
  statusFaturaStr: string | null;
  idTransportador: number | null;
  nome: string | null;
  cpfCnpj: number | null;
  numeroPedidoOrgao: string | null;
  itensFaturamento: Array<LicencaFaturamentoPayload> | null;
  groupIndex?: number | null;
}

const useLicencaFaturamentoService = () => {
  const {
    data,
    loading,
    get,
    put,
    downloadFileNewTab,
    downloadFile
  } = useApiWithAuth();

  const downloadFaturamento = (
    filter: LicencaFaturamentoFilterPayload,
    newTab?: boolean
  ): Promise<void> => {
    return (newTab ? downloadFileNewTab : downloadFile)(
      `/faturamento/download`,
      {
        ...clearObject(filter)
      }
    );
  };

  /**
   * Planilha de fechamento mensal (xlsx) da competência informada, no layout do Google Drive.
   * Endpoint restrito a root; qualquer dia do mês serve como competência.
   */
  const downloadPlanilhaFaturamento = (periodo?: Date): Promise<void> => {
    return downloadFile(`/faturamento/planilha`, { periodo });
  };

  const getTiposLicencaFaturamentoToCache = (): Promise<void> => {
    return get(`/faturamento/tipo`);
  };

  const searchLicencaFaturamento = (
    filter: LicencaFaturamentoFilterPayload,
    page: number
  ): Promise<void> => {
    return get("/faturamento/cliente", {
      ...clearObject(filter),
      ...{
        page: page
      }
    });
  };

  const searchAetsToFaturar = (page: number): Promise<void> => {
    return get("/faturamento/AetsToFaturar", {
      ...{
        page: page
      }
    });
  };

  const getTiposLicencaFaturamento = (): Array<TipoLicencaFaturamentoPayload> => {
    return useCache().getTiposLicencaFaturamento();
  };

  const getIdLicenca = (entity?: LicencaFaturamentoPayload) => {
    if (!entity) {
      return "";
    }
    return entity.idLicenca;
  };

  const getFlTipoItem = (entity?: LicencaFaturamentoPayload) => {
    if (!entity) {
      return "";
    }
    return entity.flTipoItemStr;
  };

  const getStatusFatura = (entity?: LicencaFaturamentoPayload) => {
    if (!entity) {
      return "";
    }
    return entity.statusFaturaStr;
  };

  const getNomeTransportador = (entity?: LicencaFaturamentoPayload) => {
    if (!entity) {
      return "";
    }
    return entity.nome;
  };

  const getCpfCnpj = (entity?: LicencaFaturamentoPayload) => {
    if (!entity) {
      return "";
    }
    return entity.cpfCnpj;
  };

  const getNumeroPedidoOrgao = (entity?: LicencaFaturamentoPayload) => {
    if (!entity) {
      return ";";
    }
    return entity.numeroPedidoOrgao;
  };

  const salvarStatusItem = async (item: LicencaFaturamentoPayload) => {
    try {
      const dto = {
        idFaturamentoItem: item.idFaturamentoItem,
        flTipoItemStr: item.flTipoItemStr,
        idFaturamento: item.idFaturamento,
        idLicencaOrgao: item.idLicencaOrgao,
        idLicenca: item.idLicenca,
        idOrgao: item.idOrgao,
        statusFaturaStr: item.statusFaturaStr
      };
      console.log("item: ", dto);
      await put(`/faturamento/itens/${item.idFaturamentoItem}`, dto);
    } catch (erro) {
      console.error(erro);
    }
  };

  return {
    data,
    loading,
    getTiposLicencaFaturamentoToCache,
    getTiposLicencaFaturamento,
    getIdLicenca,
    getFlTipoItem,
    getStatusFatura,
    getNomeTransportador,
    getCpfCnpj,
    getNumeroPedidoOrgao,
    downloadFaturamento,
    downloadPlanilhaFaturamento,
    searchLicencaFaturamento,
    searchAetsToFaturar,
    salvarStatusItem
  };
};

export {
  useLicencaFaturamentoService,
  LicencaFaturamentoPayload,
  LicencaFaturamentoFilterPayload,
  LicencaFaturamentoFilterReturnPayload,
  TipoLicencaFaturamentoPayload
};
